import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { popperOpen, isEnterActionTarget } from "@/lib/enterAction";

const WIDTHS = {
  md: "sm:max-w-[540px]",
  lg: "sm:max-w-[680px]",
  xl: "sm:max-w-[900px]",
  full: "sm:max-w-[1040px]",
};

function BodySkeleton() {
  return (
    <div className="space-y-5" aria-hidden data-testid="drawer-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-24 rounded bg-white/[0.06]" />
          <div className="h-10 w-full rounded-md bg-white/[0.04]" />
        </div>
      ))}
    </div>
  );
}

// Centered modal (Bulk-Postback style): smooth zoom/fade, closes via X, backdrop click or
// Esc. Pass `dirty` to guard against losing unsaved edits on close. API is unchanged so every
// existing consumer becomes a centered modal automatically.
export function FormDrawer({
  open, onOpenChange, title, subtitle, size = "lg", children,
  onSave, saving, saveLabel = "Save", saveDisabled, footer, hideFooter, testId, dirty = false,
}) {
  const [ready, setReady] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
      return () => cancelAnimationFrame(raf);
    }
    const t = setTimeout(() => { setReady(false); setConfirm(false); }, 220);
    return () => clearTimeout(t);
  }, [open]);

  // Enter triggers the primary action (Save/Create/Update) from text fields AND from closed
  // select/date triggers — after picking a value, Enter saves instead of re-opening the select.
  // While a dropdown popover is open, Enter keeps confirming the selection inside it.
  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey || !onSave) return;
    if (popperOpen()) return;
    if (!isEnterActionTarget(e.target)) return;
    e.preventDefault();
    if (!saving && !saveDisabled) onSave();
  };

  const requestClose = () => { if (dirty) setConfirm(true); else onOpenChange(false); };
  // X, backdrop and Esc all route through onOpenChange(false) — intercept once.
  const handleOpenChange = (next) => { if (!next) requestClose(); else onOpenChange(true); };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          data-testid={testId}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-white/[0.1] bg-[#141417] shadow-2xl outline-none",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            WIDTHS[size],
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-white/[0.08] px-6 py-4">
            <div className="min-w-0">
              <Dialog.Title className="font-display text-lg font-bold tracking-tight text-foreground">
                {title}
              </Dialog.Title>
              {subtitle && (
                <Dialog.Description className="mt-0.5 truncate text-sm text-muted-foreground">
                  {subtitle}
                </Dialog.Description>
              )}
            </div>
            <button
              type="button"
              onClick={requestClose}
              data-testid="drawer-close"
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5" onKeyDown={onKeyDown}>{ready ? children : <BodySkeleton />}</div>

          {/* Footer */}
          {!hideFooter && (
            <div className="flex items-center justify-end gap-2 border-t border-white/[0.08] px-6 py-4">
              {footer || (
                <>
                  <Button variant="ghost" onClick={requestClose} className="text-muted-foreground hover:text-foreground" data-testid="drawer-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => { try { await onSave?.(); } catch (_) { /* API errors surface via mutation onError toast; swallow here to avoid the dev error overlay */ } }}
                    disabled={saving || saveDisabled}
                    data-testid="drawer-save"
                    className="bg-white text-black hover:bg-white/90"
                  >
                    {saving ? "Saving..." : saveLabel}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Unsaved-changes guard */}
          {confirm && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 p-6" data-testid="unsaved-confirm">
              <div className="w-full max-w-sm rounded-xl border border-white/[0.1] bg-[#1a1a1e] p-5 shadow-2xl">
                <h3 className="text-base font-semibold text-foreground">Unsaved changes</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">Unsaved changes will be lost. Close anyway?</p>
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setConfirm(false)} data-testid="unsaved-keep" className="text-muted-foreground hover:text-foreground">Keep editing</Button>
                  <Button onClick={() => { setConfirm(false); onOpenChange(false); }} data-testid="unsaved-discard" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Close anyway</Button>
                </div>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
